<template>
  <div class="carousel">
    <div class="carousel__container" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
      <div 
        v-for="(slide, index) in slides" 
        :key="index"
        class="carousel__slide"
      >
        <slot name="slide" :slide="slide" :index="index"></slot>
      </div>
    </div>

    <div class="carousel__controls">
      <button 
        class="carousel__control carousel__control--prev"
        @click="prevSlide"
        :disabled="currentSlide === 0"
      >
        <span class="visually-hidden">Previous slide</span>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="carousel__indicators">
        <button
          v-for="(_, index) in slides"
          :key="index"
          class="carousel__indicator"
          :class="{ 'carousel__indicator--active': currentSlide === index }"
          @click="goToSlide(index)"
        >
          <span class="visually-hidden">Go to slide {{ index + 1 }}</span>
        </button>
      </div>

      <button 
        class="carousel__control carousel__control--next"
        @click="nextSlide"
        :disabled="currentSlide === slides.length - 1"
      >
        <span class="visually-hidden">Next slide</span>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'

const props = defineProps({
  slides: {
    type: Array,
    required: true
  },
  initialSlide: {
    type: Number,
    default: 0
  }
})

const currentSlide = ref(props.initialSlide)

const nextSlide = () => {
  if (currentSlide.value < props.slides.length - 1) {
    currentSlide.value++
  }
}

const prevSlide = () => {
  if (currentSlide.value > 0) {
    currentSlide.value--
  }
}

const goToSlide = (index) => {
  currentSlide.value = index
}

// Watch for changes in initialSlide prop
watch(() => props.initialSlide, (newValue) => {
  currentSlide.value = newValue
})
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/scss/abstracts/variables" as *;
@use "@/assets/scss/abstracts/mixins" as *;

.carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: $color-gray-light;
  margin-top: 2rem;
  margin-bottom: 3rem;

  &__container {
    display: flex;
    transition: transform 0.3s ease-in-out;
  }

  &__slide {
    flex: 0 0 100%;
    width: 100%;
    /**
    &:hover {
      transform: translateY(-5px);
      box-shadow: $shadow-md;
      color: color.adjust($color-primary, $lightness: -10%);
    }
    */
  }

  &__controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: $spacing-md;
  }

  &__control {
    background: none;
    border: none;
    cursor: pointer;
    padding: $spacing-sm;
    color: $color-primary;
    transition: color $transition-base;

    &:hover:not(:disabled) {
      color: color.adjust($color-primary, $lightness: -10%);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    svg {
      display: block;
    }
  }

  &__indicators {
    display: flex;
    gap: $spacing-sm;
  }

  &__indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: $color-gray-light;
    border: none;
    cursor: pointer;
    transition: background-color $transition-base;

    &--active {
      background-color: $color-primary;
    }

    &:hover {
      background-color: $color-primary;
    }
  }

  &__button {
    &:hover {
      background-color: color.adjust($color-primary, $lightness: -10%);
    }
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style> 